import { useEffect, useRef } from "react";

/**
 * Animated LED pixel backdrop — premium version.
 * • Two counter-propagating radial waves (top-right + bottom-left) create an interference pattern
 * • Slow expanding "pulse ring" every ~6s adds a hero moment
 * • Mouse hover lights up nearby dots
 * • Dual-tone: brand blue with a subtle gold accent on peak dots
 * Respects prefers-reduced-motion.
 */
export const LedBackdrop = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let running = true;
    const start = performance.now();

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();

    const spacing = 24 * dpr;
    const dotBase = 1.7 * dpr;
    const brand = "0, 67, 156";
    const gold = "168, 137, 88";

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) * dpr;
      mouseRef.current.y = (e.clientY - rect.top) * dpr;
      mouseRef.current.active = true;
    };
    const onMouseLeave = () => {
      mouseRef.current.active = false;
    };

    const draw = (t) => {
      if (!running) return;
      const elapsed = t - start;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;

      // Two anchor points for counter-propagating waves
      const cx1 = w * 0.78;
      const cy1 = h * 0.22;
      const cx2 = w * 0.12;
      const cy2 = h * 0.85;

      // Expanding pulse ring — every ~6 s emit a fresh ring
      const pulseCycle = 6000;
      const pulseProgress = (elapsed % pulseCycle) / pulseCycle; // 0..1
      const ringOrigin = { x: w * 0.5, y: h * 0.4 };
      const maxRing = Math.sqrt(w * w + h * h) * 0.6;
      const ringRadius = pulseProgress * maxRing;
      const ringOpacity = (1 - pulseProgress) * 0.5;
      const ringThickness = 60 * dpr;

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mouseActive = mouseRef.current.active;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing;
          const y = j * spacing;

          // Wave 1 — top-right anchor, outward
          const d1 = Math.hypot(x - cx1, y - cy1);
          const w1 = prefersReducedMotion
            ? 0.4
            : Math.sin(elapsed * 0.0011 - d1 * 0.015) * 0.5 + 0.5;

          // Wave 2 — bottom-left anchor, reverse phase
          const d2 = Math.hypot(x - cx2, y - cy2);
          const w2 = prefersReducedMotion
            ? 0.25
            : Math.sin(elapsed * 0.0007 + d2 * 0.011) * 0.5 + 0.5;

          const wave = w1 * 0.65 + w2 * 0.45;

          // Expanding ring boost — dots within the ring band light up
          let ringBoost = 0;
          if (!prefersReducedMotion) {
            const dr = Math.hypot(x - ringOrigin.x, y - ringOrigin.y);
            const distFromRing = Math.abs(dr - ringRadius);
            if (distFromRing < ringThickness) {
              ringBoost = (1 - distFromRing / ringThickness) * ringOpacity;
            }
          }

          // Mouse influence
          let mouseBoost = 0;
          if (mouseActive) {
            const md = Math.hypot(x - mx, y - my);
            const radius = 220 * dpr;
            if (md < radius) {
              mouseBoost = (1 - md / radius) * 1.0;
            }
          }

          const totalBoost = ringBoost + mouseBoost;
          const alpha = Math.min(0.78, wave * 0.22 + totalBoost * 0.7 + 0.04);
          const size = dotBase * (1 + totalBoost * 2.2);

          // Peak dots get a gold tint; the rest stay brand blue
          const isPeak = totalBoost > 0.35 || wave > 0.85;
          const color = isPeak ? gold : brand;

          ctx.fillStyle = `rgba(${color}, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-auto"
      aria-hidden
      style={{ mixBlendMode: "multiply" }}
    />
  );
};

export default LedBackdrop;
